import { equals } from '@0xtorch/big-decimal'
import type { Action, Transfer } from '@0xtorch/core'
import { zeroAddress } from 'viem'
import type { Chain } from '../../../chain'
import type { LowerHex } from '../../../types'
import type { TransactionDecoded } from '../../../types/transactionDecoded'

/** wrapped token の Deposit event signature。 */
const wrappedTokenDepositSignature =
  '0xe1fffcc4923d04b559f4d29a8bfc6cda04eb5b0d3c460751c2402c5c5cc9109c'
/** wrapped token の Withdrawal event signature。 */
const wrappedTokenWithdrawalSignature =
  '0x7fcf532c15f0a6db0bd6d0e038bea71d30d808c7d98cb3bf7268a95bf5081b65'

type CreateActionsByWrappedTokenAnalyzerParameters = {
  readonly chain: Chain
  readonly transaction: TransactionDecoded
  readonly transfers: readonly Transfer[]
  readonly relatedAddresses: Set<LowerHex>
  readonly source: string
}

/**
 * チェーン定義済み wrapped token の Deposit / Withdrawal を解析する。
 *
 * @param parameters.chain - 解析対象チェーン
 * @param parameters.transaction - デコード済み EVM transaction
 * @param parameters.transfers - transaction から抽出した transfer
 * @param parameters.relatedAddresses - 同期対象アドレス
 * @param parameters.source - action の参照元ID
 * @returns wrap / unwrap と断定できる場合は action、それ以外は undefined
 */
export const createActionsByWrappedTokenAnalyzer = ({
  chain,
  transaction,
  transfers,
  relatedAddresses,
  source,
}: CreateActionsByWrappedTokenAnalyzerParameters):
  | readonly Action[]
  | undefined => {
  const wrappedTokenLogs = transaction.logs.filter(
    (log) =>
      chain.wrappedTokenAddresses.has(log.address) &&
      (log.topics[0] === wrappedTokenDepositSignature ||
        log.topics[0] === wrappedTokenWithdrawalSignature),
  )
  if (wrappedTokenLogs.length !== 1) {
    return undefined
  }

  const wrappedTokenLog = wrappedTokenLogs[0]
  if (wrappedTokenLog === undefined) {
    return undefined
  }
  const isWrap = wrappedTokenLog.topics[0] === wrappedTokenDepositSignature
  const wrappedTokenTransfer = transaction.erc20Transfers.find(
    (transfer) =>
      transfer.token.address === wrappedTokenLog.address &&
      (isWrap
        ? transfer.from === zeroAddress && relatedAddresses.has(transfer.to)
        : transfer.to === zeroAddress && relatedAddresses.has(transfer.from)),
  )
  if (wrappedTokenTransfer === undefined) {
    return undefined
  }

  const expectedNativeDirection = isWrap ? 'out' : 'in'
  const expectedWrappedTokenDirection = isWrap ? 'in' : 'out'
  const actionTransfers = transfers.filter(
    (transfer) => transfer.direction !== 'none',
  )
  if (actionTransfers.length !== 2) {
    return undefined
  }
  const nativeTransfer = actionTransfers.find(
    (transfer) =>
      transfer.direction === expectedNativeDirection &&
      transfer.asset.id === chain.nativeToken.currency.id,
  )
  const wrappedTransfer = actionTransfers.find(
    (transfer) =>
      transfer.direction === expectedWrappedTokenDirection &&
      transfer.asset.id === wrappedTokenTransfer.token.currency.id,
  )
  if (
    nativeTransfer === undefined ||
    wrappedTransfer === undefined ||
    !equals(nativeTransfer.amount, wrappedTransfer.amount)
  ) {
    return undefined
  }

  return [
    {
      app: transaction.to?.app ?? transaction.from.app,
      evidence: 'contract',
      order: 0,
      source,
      timestamp: transaction.timestamp,
      transfers: [nativeTransfer, wrappedTransfer],
      type: isWrap ? 'wrap' : 'unwrap',
    },
  ]
}
